/**
 * Care Time section for the intake Details step.
 *
 * Two controlled minute inputs (call time + documentation time) bound
 * directly to the intake form's `care-time-*` fields. On submit the encounter
 * transaction-bundle builder turns these into care-time `Observation`s — there
 * is no separate write. Call time defaults from the encounter `period` (the
 * canonical call timing the scribe pipeline fills) and stays in lock-step
 * until the clinician edits it.
 *
 * @agentic connections
 */

import { useEffect, useRef, useState } from "react";
import { Card, Group, Stack, Text } from "@mantine/core";
import { IconClockHour4 } from "@tabler/icons-react";
import type { UseFormReturnType } from "@mantine/form";
import { CareTimeInlineFields } from "@/components/domain/care-time/CareTimeInlineFields";
import { DialpadCallPicker } from "@/components/domain/care-time/DialpadCallPicker";
import type { RecentCall } from "@/hooks/domain/useRecentPatientCalls";

/**
 * Form shape owning the care-time fields. Both intake form data types satisfy
 * this via the `CareTimeFields` mixin.
 * @agentic connections
 */
type CareTimeCapableFormData = {
  period?: { start?: string; end?: string };
  "care-time-call-minutes"?: number;
  "care-time-call-notes"?: string;
  "care-time-documentation-minutes"?: number;
  "care-time-documentation-notes"?: string;
};

/** Props for {@link IntakeCareTimeSection}. @agentic connections */
export type IntakeCareTimeSectionProps<T extends CareTimeCapableFormData> = {
  form: UseFormReturnType<T>;
  /** Patient being documented, for the recent-call picker. */
  patientId?: string;
};

/**
 * Derive whole-minute call duration from the encounter period. Rounds up so a
 * sub-minute call still bills 1 minute, mirroring the bundle builder.
 * @agentic connections
 */
const minutesFromPeriod = (
  start: string | undefined,
  end: string | undefined,
): number | undefined => {
  if (!start || !end) return undefined;
  const startMs = Date.parse(start);
  const endMs = Date.parse(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return undefined;
  const durationMs = endMs - startMs;
  if (durationMs <= 0) return undefined;
  return Math.max(1, Math.ceil(durationMs / 60_000));
};

/**
 * Renders the intake Care Time capture section bound to the form's
 * `care-time-*` fields.
 * @agentic connections
 */
export const IntakeCareTimeSection = <T extends CareTimeCapableFormData>({
  form,
  patientId,
}: IntakeCareTimeSectionProps<T>) => {
  // Freeze the period-derived default once the clinician edits call time, and
  // never auto-derive when a value was already hydrated (edit / draft resume).
  const callTouchedRef = useRef<boolean>(
    typeof form.values["care-time-call-minutes"] === "number",
  );

  const [selectedCommunicationId, setSelectedCommunicationId] = useState<
    string | undefined
  >(undefined);

  const handleSelectCall = (call: RecentCall | undefined) => {
    setSelectedCommunicationId(call?.communicationId);
    if (call) {
      callTouchedRef.current = true;
      form.setFieldValue(
        "care-time-call-minutes" as never,
        call.minutes as never,
      );
    }
  };

  const periodStart = form.values.period?.start;
  const periodEnd = form.values.period?.end;
  const callMinutes = form.values["care-time-call-minutes"];
  const docMinutes = form.values["care-time-documentation-minutes"];

  useEffect(() => {
    if (callTouchedRef.current) return;
    const derived = minutesFromPeriod(periodStart, periodEnd);
    if (derived !== undefined && derived !== callMinutes) {
      form.setFieldValue("care-time-call-minutes" as never, derived as never);
    }
    // `form` is intentionally omitted: Mantine returns a new form object each
    // render, so including it would re-run this effect every render and loop
    // via setFieldValue. The form's setFieldValue identity is stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodStart, periodEnd, callMinutes]);

  return (
    <Card withBorder padding="md" radius="md" mt="md">
      <Group gap="xs" mb="sm">
        <IconClockHour4 size={16} />
        <Text size="sm" fw={600}>
          Care time
        </Text>
      </Group>
      <Group align="flex-start" wrap="nowrap" gap="md">
        <div style={{ flex: 1 }}>
          <Stack gap="sm">
            <CareTimeInlineFields
              minutes={callMinutes}
              onMinutesChange={(value) => {
                callTouchedRef.current = true;
                form.setFieldValue(
                  "care-time-call-minutes" as never,
                  value as never,
                );
              }}
              notes={form.values["care-time-call-notes"] ?? ""}
              onNotesChange={(value) =>
                form.setFieldValue(
                  "care-time-call-notes" as never,
                  value as never,
                )
              }
              label="Call time"
              helperText={
                periodStart && periodEnd
                  ? "Pre-filled from the call's start and end time; editable."
                  : "Minutes spent on the intake call."
              }
            />
            <DialpadCallPicker
              patientId={patientId}
              selectedCommunicationId={selectedCommunicationId}
              onSelect={handleSelectCall}
            />
          </Stack>
        </div>
        <div style={{ flex: 1 }}>
          <CareTimeInlineFields
            minutes={docMinutes}
            onMinutesChange={(value) =>
              form.setFieldValue(
                "care-time-documentation-minutes" as never,
                value as never,
              )
            }
            notes={form.values["care-time-documentation-notes"] ?? ""}
            onNotesChange={(value) =>
              form.setFieldValue(
                "care-time-documentation-notes" as never,
                value as never,
              )
            }
            label="Documentation time"
            helperText="Minutes spent documenting after the call."
          />
        </div>
      </Group>
    </Card>
  );
};
